import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowLeft, Loader2, MailCheck, Send } from "lucide-react";
import { toast } from "sonner";
import { AuthLayout } from "../components/layout/AuthLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api, apiError } from "../lib/api";

const schema = z.object({
  email: z.string().min(1, "Email is required").email("Enter a valid email address"),
});

export default function ForgotPassword() {
  const [sent, setSent] = useState(null);
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(schema), defaultValues: { email: "" } });

  const onSubmit = async (values) => {
    try {
      const { data } = await api.post("/api/auth/forgot-password", { email: values.email.trim() });
      setSent(data);
      toast.success("Reset instructions sent if the account exists");
    } catch (error) {
      toast.error(apiError(error, "Could not start password reset"));
    }
  };

  if (sent) {
    return (
      <AuthLayout title="Check your inbox" subtitle="We have processed your reset request.">
        <div className="rounded-xl border border-border bg-card p-5" data-testid="forgot-password-success">
          <MailCheck className="h-6 w-6 text-[hsl(var(--profit))]" />
          <p className="mt-3 text-sm leading-relaxed">
            If an account exists for <span className="font-medium">{getValues("email")}</span>, a secure
            reset link is on its way. The link expires in 45 minutes and can be used only once.
          </p>
          {sent.emailSent === false && sent.deliveryNote ? (
            <p className="mt-4 rounded-lg border border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/10 p-3 text-xs text-[hsl(var(--warning))]">
              Delivery notice: {sent.deliveryNote}
            </p>
          ) : null}
          <Button asChild variant="secondary" className="mt-5 w-full">
            <Link to="/login" data-testid="forgot-password-back-link">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to sign in
            </Link>
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="Enter the email registered with your Invextiing account and we will send a secure reset link."
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
        <div className="space-y-2">
          <Label htmlFor="email">Email address</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            {...register("email")}
            data-testid="forgot-password-email-input"
          />
          {errors.email ? <p className="text-xs text-destructive">{errors.email.message}</p> : null}
        </div>
        <Button
          type="submit"
          className="w-full"
          disabled={isSubmitting}
          data-testid="forgot-password-submit-button"
        >
          {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
          Send reset link
        </Button>
        <p className="text-center text-xs">
          <Link
            to="/login"
            className="text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          >
            Back to sign in
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
